import React from "react";
import PropTypes from "prop-types";
import constants from "../../utils/constants";
import { defineMessages, injectIntl } from "react-intl";
import { pure } from "recompose";
import { getTranslatedDaysOfTheWeek } from "../../utils/date";
import classnames from "classnames";
import styles from "./BookableErrorMessage.scss";

export const msgs = defineMessages({
  day: {
    id: "inventory.restriction.day",
    description: "Day String",
    defaultMessage: "day",
  },
  days: {
    id: "inventory.restriction.days",
    description: "Days String",
    defaultMessage: "days",
  },
  night: {
    id: "inventory.restriction.night",
    description: "Night String",
    defaultMessage: "night",
  },
  nights: {
    id: "inventory.restriction.nights",
    description: "Nights String",
    defaultMessage: "nights",
  },
  hours: {
    id: "inventory.restriction.hours",
    description: "Hours String",
    defaultMessage: "Hours",
  },
  minStay: {
    id: "inventory.restriction.minStay",
    description: "Stay Restriction message on inventory",
    defaultMessage: "Min. Stay",
  },
  minNotice: {
    id: "inventory.restriction.minNotice",
    description: "Notice Restriction message on inventory",
    defaultMessage: "Min. Advance",
  },
  maxStay: {
    id: "inventory.restriction.maxStay",
    description: "Stay Restriction message on inventory",
    defaultMessage: "Max. Stay",
  },
  maxNotice: {
    id: "inventory.restriction.maxNotice",
    description: "Notice Restriction message on inventory",
    defaultMessage: "Max. Advance",
  },
  checkIn: {
    id: "inventory.restriction.checkIn",
    description: "checkIn Restriction message on inventory",
    defaultMessage: "Check-in on",
  },
  checkOut: {
    id: "inventory.restriction.checkOut",
    description: "checkIn Restriction message on inventory",
    defaultMessage: "Check-out on",
  },
  fullBooked: {
    id: "room_selecton.calendar.fullBooked",
    description: "fullBooked",
    defaultMessage: "Fully booked",
  },
  [constants.BOOKABLE_STATUS.UNAVAILABLE]: {
    id: "inventory.restriction.unavailable",
    description: "Unavailable message on inventory",
    defaultMessage: "SOLD OUT",
  },
  validity: {
    id: "inventory.restriction.validity",
    description: "validity label",
    defaultMessage: "Validity",
  },
  restricted: {
    id: "inventory.restriction.restricted",
    description: "restricted",
    defaultMessage: "Restricted",
  },
  bookable: {
    id: "inventory.restriction.bookable",
    description: "bookable label",
    defaultMessage: "Bookable",
  },
  unavailable_for_whole_range: {
    id: "inventory.restriction.unavailable_for_whole_range",
    description:
      "Message shown in hotel calendar when the hotel is not available for reservation through the whole range selected," +
      "but it is available for partial ranges or for other occupations.",
    defaultMessage: "Available by combining several types of accommodation. Contact us.",
  },
  hotelCardRestricted: {
    id: "room_selection.hotel_card.restricted",
    description: "Hotel Card Restricted",
    defaultMessage: "view options",
  },
});

function formatRestrictedValue(filterType, value) {
  switch (filterType) {
    case constants.ARI_VALUE_TYPE.MAX_NOTICE: {
      return value >= 24 ? (value / 24).toFixed(0) : value;
    }
    case constants.ARI_VALUE_TYPE.MIN_NOTICE: {
      return value >= 24 ? (value / 24).toFixed(0) : value;
    }
    case constants.ARI_VALUE_TYPE.MAX_STAY: {
      return value;
    }
    case constants.ARI_VALUE_TYPE.MIN_STAY: {
      return value;
    }
    case constants.ARI_VALUE_TYPE.CHECK_IN_CHECK_OUT: {
      return value;
    }
    default: {
      return "";
    }
  }
}

function createUnavailableMessage(status, showDays, days, formatMessage) {
  if (showDays) {
    return formatMessage(msgs.fullBooked) + ": " + days;
  }
  return formatMessage(msgs[status]);
}

function buildCheckinCheckoutValue(statusValue, formatDate) {
  const checkInDaysInfo = statusValue.get
    ? statusValue.get("checkInDays")
    : statusValue.checkInDays;
  const checkOutDaysInfo = statusValue.get
    ? statusValue.get("checkOutDays")
    : statusValue.checkOutDays;
  if (checkInDaysInfo) {
    return getTranslatedDaysOfTheWeek(formatDate, checkInDaysInfo).join(",");
  } else if (checkOutDaysInfo) {
    return getTranslatedDaysOfTheWeek(formatDate, checkOutDaysInfo).join(",");
  }
}

function buildCheckinCheckoutLabel(statusValue, formatMessage) {
  const checkInDaysInfo = statusValue.get
    ? statusValue.get("checkInDays")
    : statusValue.checkInDays;
  const checkOutDaysInfo = statusValue.get
    ? statusValue.get("checkOutDays")
    : statusValue.checkOutDays;
  if (checkInDaysInfo) {
    return formatMessage(msgs.checkIn);
  } else if (checkOutDaysInfo) {
    return formatMessage(msgs.checkOut);
  }
}

function buildDateRangeValue(statusValue, valueType, formatDate) {
  const validSinceInfo = statusValue.get ? statusValue.get("validSince") : statusValue.validSince;
  const validUntilInfo = statusValue.get ? statusValue.get("validUntil") : statusValue.validUntil;
  const availableSinceInfo = statusValue.get
    ? statusValue.get("availableSince")
    : statusValue.availableSince;
  const availableUntilInfo = statusValue.get
    ? statusValue.get("availableUntil")
    : statusValue.availableUntil;
  let startDate = null;
  let endDate = null;
  if (valueType === constants.ARI_VALUE_TYPE.VALIDITY) {
    if (validSinceInfo) {
      startDate = formatDate(validSinceInfo);
    }
    if (validUntilInfo) {
      endDate = formatDate(validUntilInfo);
    }
  }
  if (valueType === constants.ARI_VALUE_TYPE.CONTRACT_PERIOD) {
    if (availableSinceInfo) {
      startDate = formatDate(availableSinceInfo);
    }
    if (availableUntilInfo) {
      endDate = formatDate(availableUntilInfo);
    }
  }
  if (startDate && endDate) {
    return `${startDate} - ${endDate}`;
  }
  return "";
}

function buildMessageValue(valueType, statusValue, formatMessage, formatDate) {
  const components = [];
  switch (valueType) {
    case constants.ARI_VALUE_TYPE.MIN_STAY:
    case constants.ARI_VALUE_TYPE.MAX_STAY:
      components.push(statusValue);
      if (statusValue > 1) {
        components.push(formatMessage(msgs.nights));
      } else {
        components.push(formatMessage(msgs.night));
      }
      break;
    case constants.ARI_VALUE_TYPE.MIN_NOTICE:
    case constants.ARI_VALUE_TYPE.MAX_NOTICE:
      const formattedValue = formatRestrictedValue(valueType, statusValue);
      components.push(formattedValue);
      if (statusValue < 24) {
        components.push(formatMessage(msgs.hours));
      } else {
        if (parseInt(formattedValue, 10) > 1) {
          components.push(formatMessage(msgs.days));
        } else {
          components.push(formatMessage(msgs.day));
        }
      }
      break;
    case constants.ARI_VALUE_TYPE.CHECK_IN_CHECK_OUT:
      components.push(buildCheckinCheckoutValue(statusValue, formatDate));
      break;
    case constants.ARI_VALUE_TYPE.VALIDITY:
      components.push(buildDateRangeValue(statusValue, valueType, formatDate));
      break;
    case constants.ARI_VALUE_TYPE.CONTRACT_PERIOD:
      components.push(buildDateRangeValue(statusValue, valueType, formatDate));
      break;
    default: {
      return "";
    }
  }
  return components.join(" ");
}

function buildMessageLabel(valueType, statusValue, formatMessage) {
  const components = [];
  switch (valueType) {
    case constants.ARI_VALUE_TYPE.MAX_NOTICE:
      components.push(formatMessage(msgs.maxNotice));
      break;
    case constants.ARI_VALUE_TYPE.MAX_STAY:
      components.push(formatMessage(msgs.maxStay));
      break;
    case constants.ARI_VALUE_TYPE.MIN_NOTICE:
      components.push(formatMessage(msgs.minNotice));
      break;
    case constants.ARI_VALUE_TYPE.MIN_STAY:
      components.push(formatMessage(msgs.minStay));
      break;
    case constants.ARI_VALUE_TYPE.AVAILABILITY:
    case constants.ARI_VALUE_TYPE.LOCK:
    case constants.ARI_VALUE_TYPE.RATE:
    case constants.ARI_VALUE_TYPE.OCCUPATION:
      components.push(formatMessage(msgs[constants.BOOKABLE_STATUS.UNAVAILABLE]));
      break;
    case constants.ARI_VALUE_TYPE.CHECK_IN_CHECK_OUT:
      components.push(buildCheckinCheckoutLabel(statusValue, formatMessage));
      break;
    case constants.ARI_VALUE_TYPE.VALIDITY:
      components.push(formatMessage(msgs.validity));
      break;
    case constants.ARI_VALUE_TYPE.CONTRACT_PERIOD:
      components.push(formatMessage(msgs.bookable));
      break;
    case constants.ARI_VALUE_TYPE.CALENDAR.UNAVAILABLE_FOR_WHOLE_RANGE:
      components.push(formatMessage(msgs.unavailable_for_whole_range));
      break;
    default: {
      return "";
    }
  }

  return components.join(" ");
}

function createRestrictedMessage(
  statusFilterType,
  statusValue,
  formatDate,
  formatMessage,
  labelClassName,
  valueClassName,
  hotelCard,
  colon
) {
  if (
    typeof statusFilterType === "undefined" ||
    statusFilterType === null ||
    typeof statusValue === "undefined" ||
    statusValue === null
  ) {
    return (
      <span>
        <span
          className={classnames(
            "wbkv9-BookableErrorMessage-singleLineLabel",
            labelClassName,
            "singleLine"
          )}>
          {(hotelCard && formatMessage(msgs.hotelCardRestricted)) || formatMessage(msgs.restricted)}
          &nbsp;
        </span>
      </span>
    );
  }
  const messageLabel = buildMessageLabel(statusFilterType, statusValue, formatMessage);
  const messageValue = buildMessageValue(statusFilterType, statusValue, formatMessage, formatDate);
  return (
    <span>
      <span className={classnames("wbkv9-BookableErrorMessage-label", labelClassName)}>
        {messageLabel}
      </span>
      {colon && <span>: </span>}
      <span className={classnames("wbkv9-BookableErrorMessage-value", valueClassName)}>
        {messageValue}
      </span>
    </span>
  );
}

function BookableErrorMessage({
  status,
  valueType,
  value,
  formatDate,
  formatMessage,
  showDays,
  days,
  labelClassName,
  valueClassName,
  hotelCard,
  colon,
}) {
  if (
    status === constants.BOOKABLE_STATUS.AVAILABLE &&
    (typeof value === "undefined" || value === null)
  ) {
    return null;
  }
  return (
    <div
      className={classnames(
        "wbkv9-BookableErrorMessage",
        styles.container,
        (valueType === constants.ARI_VALUE_TYPE.CALENDAR.UNAVAILABLE_FOR_WHOLE_RANGE ||
          status === constants.BOOKABLE_STATUS.RESTRICTED) &&
          styles.wrap
      )}>
      {status === constants.BOOKABLE_STATUS.AVAILABLE &&
        createRestrictedMessage(
          valueType,
          value,
          formatDate,
          formatMessage,
          labelClassName,
          valueClassName,
          hotelCard,
          colon
        )}
      {status === constants.BOOKABLE_STATUS.RESTRICTED &&
        createRestrictedMessage(
          valueType,
          value,
          formatDate,
          formatMessage,
          labelClassName,
          valueClassName,
          hotelCard,
          colon
        )}
      {status === constants.BOOKABLE_STATUS.UNAVAILABLE &&
        createUnavailableMessage(status, showDays, days, formatMessage)}
    </div>
  );
}

export function BookableErrorMessageNoIntl({
  status,
  valueType,
  value,
  formatDate,
  formatMessage,
  showDays,
  days,
  labelClassName,
  valueClassName,
  hotelCard,
  colon,
}) {
  return (
    <BookableErrorMessage
      status={status}
      valueType={valueType}
      value={value}
      formatDate={formatDate}
      formatMessage={formatMessage}
      showDays={showDays}
      days={days}
      labelClassName={labelClassName}
      valueClassName={valueClassName}
      hotelCard={hotelCard}
      colon={colon}
    />
  );
}

function BookableErrorMessageIntl({
  status,
  valueType,
  value,
  intl: { formatDate, formatMessage },
  showDays,
  days,
  labelClassName,
  valueClassName,
  hotelCard,
  colon,
}) {
  return (
    <BookableErrorMessage
      status={status}
      valueType={valueType}
      value={value}
      formatDate={formatDate}
      formatMessage={formatMessage}
      showDays={showDays}
      days={days}
      labelClassName={labelClassName}
      valueClassName={valueClassName}
      hotelCard={hotelCard}
      colon={colon}
    />
  );
}

BookableErrorMessage.propTypes = {
  status: PropTypes.string.isRequired,
  valueType: PropTypes.string.isRequired,
  value: PropTypes.any,
  showDays: PropTypes.bool,
  days: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  labelClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  valueClassName: PropTypes.string,
  hotelCard: PropTypes.bool,
  colon: PropTypes.bool,
};

export default injectIntl(pure(BookableErrorMessageIntl));
